import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Paper, Grid,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel,
  Checkbox, Switch, Alert, Collapse, CircularProgress, useTheme, Divider,
  Accordion, AccordionSummary, AccordionDetails, Chip, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { createDCPJob, fetchDCPAvailableFiles } from '../store/slices/dcpJobsSlice';
import PageHeader from '../components/PageHeader';
import PipelineProgress from '../components/PipelineProgress';
import api from '../services/api';
import { useUploadManager } from '../contexts/UploadManagerContext';
import { isValidName, sanitizeName, isValidFilename } from '../utils/createJobValidation';
import { decideSourceUpload } from '../utils/sourceUploadDecision';

// SMPTE ST 429-2 Configuration 4 (Wild Track Format) — 8ch MXF
// Per Netflix DCP spec and SMPTE RDD 52:2020
const DCP_CHANNELS_51 = [
  { index: 1, label: 'Left', abbr: 'L', sourceDefault: 1 },
  { index: 2, label: 'Right', abbr: 'R', sourceDefault: 2 },
  { index: 3, label: 'Center', abbr: 'C', sourceDefault: 3 },
  { index: 4, label: 'LFE', abbr: 'LFE', sourceDefault: 4 },
  { index: 5, label: 'Left Surround', abbr: 'Ls', sourceDefault: 5 },
  { index: 6, label: 'Right Surround', abbr: 'Rs', sourceDefault: 6 },
  { index: 7, label: 'Hearing Impaired', abbr: 'HI', sourceDefault: 'silent' },
  { index: 8, label: 'Visually Impaired', abbr: 'VI', sourceDefault: 'silent' },
];

const DCP_CHANNELS_STEREO = [
  { index: 1, label: 'Left', abbr: 'L', sourceDefault: 1 },
  { index: 2, label: 'Right', abbr: 'R', sourceDefault: 2 },
  { index: 3, label: 'Center', abbr: 'C', sourceDefault: 'silent' },
  { index: 4, label: 'LFE', abbr: 'LFE', sourceDefault: 'silent' },
  { index: 5, label: 'Left Surround', abbr: 'Ls', sourceDefault: 'silent' },
  { index: 6, label: 'Right Surround', abbr: 'Rs', sourceDefault: 'silent' },
  { index: 7, label: 'Hearing Impaired', abbr: 'HI', sourceDefault: 'silent' },
  { index: 8, label: 'Visually Impaired', abbr: 'VI', sourceDefault: 'silent' },
];

const CONTENT_TYPES = [
  { value: 'FTR', label: 'FTR — Feature' },
  { value: 'SHR', label: 'SHR — Short' },
  { value: 'TLR', label: 'TLR — Trailer' },
  { value: 'TST', label: 'TST — Test' },
  { value: 'RTG', label: 'RTG — Rating' },
  { value: 'PSA', label: 'PSA — Public Service' },
  { value: 'ADV', label: 'ADV — Advertisement' },
];

// (Removed in v10.4.33: parked SOURCE_CHANNEL_OPTIONS constant —
// the runtime sourceOptions array is built dynamically from
// probeData.audio.channels in the component body, so the static
// list was always going to drift. Recoverable from git.)

// Derive DCP resolution from source — match the source native resolution
// Derive a theater-safe ISDCF title abbreviation from a free-text
// project name. The user types whatever reads well to humans
// ("Come What May"); this slug is what actually lands in the
// deliverable tar filename. Mirrors what the backend does at
// dispatch-job time, so the live preview matches reality.
function deriveTitleSlug(raw, strict) {
  if (!raw) return '';
  const cap = strict ? 14 : 20;
  return raw
    .replace(/[^A-Za-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, cap)
    .replace(/-+$/, '');
}

function deriveDCPResolution(width) {
  if (!width) return '2K';
  if (width > 2048) return '4K';
  return '2K';
}

// Derive nearest standard DCP frame rate
function deriveDCPFrameRate(fps) {
  if (!fps) return 24;
  const rounded = Math.round(fps);
  // 23.976 → 24, 29.97 → 30, etc.
  if (rounded <= 24) return 24;
  if (rounded <= 25) return 25;
  if (rounded <= 30) return 30;
  if (rounded <= 48) return 48;
  return 24;
}

function CreateDCPJob() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { availableFiles } = useSelector((state) => state.dcpJobs);

  // Basic settings — auto-derived from source, user can override in Advanced
  const [formData, setFormData] = useState({
    name: '',
    // Real film title for third-party-facing artifacts (delivery
    // receipts, etc.). Falls back to `name` server-side if blank.
    // v10.18.6+.
    film_title: '',
    container: 'flat',
    resolution: '2K',         // auto-derived from source
    frame_rate: 24,           // auto-derived from source
    standard: 'interop',  // Interop is the most compatible — fewer booth surprises
    content_type: 'FTR',
    video_bitrate: 150,
    encrypt: true,
    encrypt_picture: true,
    encrypt_sound: true,
    encrypt_text: true,
    audio_layout: '5.1',
    audio_language: 'English',
    audio_gain_db: 0,
    ffec_frame: '',
    ffmc_frame: '',
    reel_mode: 'single',
    reel_duration_minutes: 20,
    reel_break_timecodes: [],
    priority: 'normal',
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [audioMapping, setAudioMapping] = useState({});
  const [probeData, setProbeData] = useState(null);
  const [probing, setProbing] = useState(false);
  // Cinema-loudness advice from /api/jobs/available-files/{filename}/qc.
  // Populated on source select; used to auto-fill audio_gain_db with a
  // recommendation and render the explanation banner.
  const [loudnessAdvice, setLoudnessAdvice] = useState(null);
  // Stereo-downmix advice (separate concern from loudness): plain-English
  // explanation when a stereo source looks like a downmix from a
  // multi-channel master with center-summed dialog. Layered with a
  // collapsible "why does this matter?" expander.
  const [stereoAdvice, setStereoAdvice] = useState(null);
  const [stereoDetailsOpen, setStereoDetailsOpen] = useState(false);
  // Project Name length cap. Default ON (strict 14-char cinema
  // standard) so deliveries are universally safe out of the box.
  // Toggling OFF lets the user go up to 20 with a plain-English note
  // about what that means — opt-out for operators shipping to known
  // modern venues that handle longer titles.
  const [strictTitleLimit, setStrictTitleLimit] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { enqueue } = useUploadManager();

  const handleSourceUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);

    // Check the unfiltered source list to detect cross-product
    // duplicates. Decision tree (existing-here vs other-product
    // vs new) is shared with Watermarks Create via
    // utils/sourceUploadDecision.js.
    let allFiles = [];
    try {
      const res = await api.get('/api/jobs/available-files');
      allFiles = res.data?.files || [];
    } catch (_) { /* fall through */ }

    let outcome;
    try {
      outcome = await decideSourceUpload({
        file, existingFiles: allFiles, product: 'dcp', api,
      });
    } catch (e) {
      setError(`Failed to enable existing source: ${e.response?.data?.detail || e.message}`);
      return;
    }
    if (outcome === 'skip') return;
    if (outcome === 'enabled') {
      await dispatch(fetchDCPAvailableFiles());
      return;
    }

    enqueue(file, {
      flags: { watermarks: false, dcp: true },
      onComplete: () => setTimeout(() => dispatch(fetchDCPAvailableFiles()), 1000),
    });
  };

  // Track which fields the user has manually overridden
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    dispatch(fetchDCPAvailableFiles());
  }, [dispatch]);

  // v10.21.6: pre-fill Film Title with the active campaign's name so
  // delivery receipts read the actual film title instead of falling
  // back to the internal Project Name build label. The operator can
  // still edit before submit; touched ref prevents the late-arriving
  // fetch from stomping changes. Same pattern as CreateJob.jsx.
  //
  // v10.21.7: two-stage lookup — id first, list fallback when missing
  // or single-id GET fails. Mirrors the CreateJob path.
  const filmTitleTouched = useRef(false);
  useEffect(() => {
    if (filmTitleTouched.current) return;
    if (formData.film_title) return;
    const cid = (() => {
      try { return localStorage.getItem('activeCampaignId'); }
      catch (_e) { return null; }
    })();
    let cancelled = false;
    const apply = (name) => {
      if (cancelled) return;
      const clean = (name || '').trim();
      if (clean && !filmTitleTouched.current) {
        setFormData((prev) => prev.film_title ? prev : { ...prev, film_title: clean });
      }
    };
    if (cid) {
      api.get(`/api/campaigns/${cid}`)
        .then((r) => apply(r.data?.name))
        .catch(() => {
          api.get('/api/campaigns')
            .then((r) => {
              const arr = Array.isArray(r.data) ? r.data : [];
              if (arr.length === 1) apply(arr[0]?.name);
            })
            .catch(() => { /* swallow */ });
        });
    } else {
      api.get('/api/campaigns')
        .then((r) => {
          const arr = Array.isArray(r.data) ? r.data : [];
          if (arr.length === 1) apply(arr[0]?.name);
        })
        .catch(() => { /* swallow */ });
    }
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ?source=<filename> pre-selects a file from the Sources page's QC
  // drawer. Apply once the available-files list is loaded, then strip
  // the param so a refresh doesn't re-apply.
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectSource = searchParams.get('source');
  useEffect(() => {
    if (!preselectSource) return;
    if (!availableFiles?.length) return;
    const match = availableFiles.find((f) => f.filename === preselectSource);
    if (match && selectedFile !== match.filename) {
      setSelectedFile(match.filename);
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('source');
      return next;
    }, { replace: true });
  }, [preselectSource, availableFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set default audio mapping when layout changes
  useEffect(() => {
    const channels = formData.audio_layout === 'stereo' ? DCP_CHANNELS_STEREO : DCP_CHANNELS_51;
    const mapping = {};
    channels.forEach((ch) => { mapping[ch.index] = ch.sourceDefault; });
    setAudioMapping(mapping);
  }, [formData.audio_layout]);

  // Default to Interop for maximum compatibility with old projectors.
  // (Interop KDMs are supported via PKL Type patching on the encoder node.)

  // When probe data arrives, auto-fill settings from source (unless user overrode them)
  useEffect(() => {
    if (!probeData) return;
    const updates = {};

    if (probeData.video && !overrides.resolution) {
      updates.resolution = deriveDCPResolution(probeData.video.width);
    }
    if (probeData.video && !overrides.frame_rate) {
      updates.frame_rate = deriveDCPFrameRate(probeData.video.frame_rate);
    }
    if (probeData.audio?.suggested_layout && !overrides.audio_layout) {
      updates.audio_layout = probeData.audio.suggested_layout;
    }
    if (probeData.duration_seconds && !overrides.reel_mode) {
      // Default to by_duration for features (most compatible with theater
      // playback servers), single for short content.
      updates.reel_mode = probeData.duration_seconds > 1200 ? 'by_duration' : 'single';
    }

    if (Object.keys(updates).length > 0) {
      setFormData((prev) => ({ ...prev, ...updates }));
    }
  }, [probeData, overrides]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Advanced override — marks a field as user-overridden so probe doesn't reset it
  const handleOverride = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setOverrides((prev) => ({ ...prev, [field]: true }));
  };

  const handleFileSelect = async (filename) => {
    setSelectedFile(filename);

    // Auto-fill name from filename
    const baseName = filename.replace(/\.[^/.]+$/, '');
    setFormData((prev) => {
      if (!prev.name || prev.name === selectedFile?.replace(/\.[^/.]+$/, '')) {
        return { ...prev, name: baseName };
      }
      return prev;
    });

    // Reset overrides when new file is selected (re-derive from source)
    setOverrides({});

    // Probe file
    setProbing(true);
    setProbeData(null);
    setLoudnessAdvice(null);
    try {
      const { data } = await api.get(`/api/dcp-jobs/probe/${encodeURIComponent(filename)}`);
      setProbeData(data);
    } catch (_err) {
      setProbeData(null);
    } finally {
      setProbing(false);
    }

    // Pull cinema-loudness advice from the source QC sidecar in parallel.
    // Auto-fills audio_gain_db with the recommendation unless the user
    // already overrode it. The Audio Gain UI surfaces the rationale so
    // the user understands what the system is suggesting and why.
    setStereoAdvice(null);
    setStereoDetailsOpen(false);
    try {
      const { data: qc } = await api.get(`/api/jobs/available-files/${encodeURIComponent(filename)}/qc`);
      const loudAdv = qc?.cinema_loudness_advice || null;
      const stereoAdv = qc?.stereo_downmix_advice || null;
      setLoudnessAdvice(loudAdv);
      setStereoAdvice(stereoAdv);
      // Stereo-downmix advice takes precedence over generic loudness
      // advice when both fire — the downmix recommendation is more
      // specific to theatrical playback. Auto-fill the gain unless the
      // user already overrode it.
      if (!overrides.audio_gain_db) {
        const gain =
          stereoAdv?.recommended_gain_db ??
          loudAdv?.recommended_gain_db ??
          0;
        setFormData((prev) => ({ ...prev, audio_gain_db: gain }));
      }
    } catch (_) { /* QC sidecar may not exist yet; that's fine. */ }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { setError('Project name is required'); return; }
    // Same SQL/XSS guard the Watermarks Create page applies. Project
    // names land in DCP CPL/PKL XML and ISDCF tar filenames, so a
    // pasted `<script>` tag or DROP TABLE keyword would either break
    // XML serialization or trip downstream sanitizers without a
    // useful error.
    if (!isValidName(formData.name)) {
      setError(`Project name contains invalid characters. Suggested: ${sanitizeName(formData.name)}`);
      return;
    }
    if (formData.reel_mode === 'by_source_file') {
      if (selectedFiles.length < 2) { setError('Select at least 2 source files for multi-reel mode'); return; }
      // Multi-reel: every selected source filename must be safe.
      const bad = selectedFiles.filter((f) => !isValidFilename(f));
      if (bad.length > 0) {
        setError(`Source filename has illegal characters: ${bad[0]}`);
        return;
      }
    } else if (!selectedFile) {
      setError('Please select a source file'); return;
    } else if (!isValidFilename(selectedFile)) {
      setError(`Source filename has illegal characters: ${selectedFile}`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await dispatch(createDCPJob({
        ...formData,
        source_filename: formData.reel_mode === 'by_source_file' ? selectedFiles[0] : selectedFile,
        source_filenames: formData.reel_mode === 'by_source_file' ? selectedFiles : undefined,
        reel_mode: formData.reel_mode,
        reel_duration_minutes: formData.reel_mode === 'by_duration' ? formData.reel_duration_minutes : undefined,
        reel_break_timecodes: formData.reel_mode === 'by_timecode' && formData.reel_break_timecodes.length > 0 ? formData.reel_break_timecodes : undefined,
        audio_channels: 8,
        audio_mapping: audioMapping,
        audio_gain_db: parseFloat(formData.audio_gain_db) || 0,
        ffec_frame: formData.ffec_frame ? parseInt(formData.ffec_frame) : null,
        ffmc_frame: formData.ffmc_frame ? parseInt(formData.ffmc_frame) : null,
        metadata: {
          ...(formData.metadata || {}),
          source_duration_seconds: probeData?.duration_seconds || null,
          source_resolution: probeData?.video ? `${probeData.video.width}x${probeData.video.height}` : null,
          source_fps: probeData?.video?.frame_rate || null,
          source_codec: probeData?.video?.codec || null,
          source_audio_channels: probeData?.audio?.channels || null,
        },
      })).unwrap();
      navigate('/dcp');
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to create DCP job');
    } finally {
      setSubmitting(false);
    }
  };

  const currentChannels = formData.audio_layout === 'stereo' ? DCP_CHANNELS_STEREO : DCP_CHANNELS_51;
  const sourceAudioChannels = probeData?.audio?.channels || 6;
  const sourceOptions = [
    { value: 'silent', label: 'Silent' },
    ...Array.from({ length: Math.max(sourceAudioChannels, 8) }, (_, i) => ({ value: i + 1, label: `Source Ch ${i + 1}` })),
  ];

  const profileName = `SDR_Theatrical_${formData.standard}_${formData.container === 'flat' ? 'Flat' : 'Scope'}_${formData.frame_rate}_${formData.encrypt ? 'Enc' : 'Open'}_${formData.audio_layout === '5.1' ? '51' : '20'}`;

  // Source-derived info summary
  const sourceResLabel = probeData?.video ? `${probeData.video.width}x${probeData.video.height}` : null;
  const sourceFpsLabel = probeData?.video ? `${probeData.video.frame_rate} fps` : null;
  const derivedRes = probeData?.video ? deriveDCPResolution(probeData.video.width) : null;
  const derivedFps = probeData?.video ? deriveDCPFrameRate(probeData.video.frame_rate) : null;

  const fileColumns = [
    { field: 'filename', headerName: 'Filename', flex: 1 },
    {
      field: 'qc_compliance_per_product',
      headerName: 'DCP QC',
      width: 90,
      sortable: false,
      renderCell: (params) => {
        if (params.row.qc_running) {
          return <Chip size="small" label="..." variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />;
        }
        const cpp = params.row.qc_compliance_per_product
          || { dcp: params.row.qc_compliance };
        const verdict = cpp.dcp;
        if (!verdict) return <Typography variant="caption" color="text.disabled">—</Typography>;
        const map = {
          pass:        { label: '✓', color: 'success' },
          warn:        { label: '!', color: 'warning' },
          fail:        { label: '✗', color: 'error'   },
          needs_input: { label: '?', color: 'info'    },
        };
        const cfg = map[verdict] || { label: verdict, color: 'default' };
        return (
          <Tooltip
            title={`DCP-side QC: ${verdict}${verdict === 'needs_input' ? ' (FFEC/FFMC missing)' : ''}`}
            arrow
          >
            <Chip size="small" label={cfg.label} color={cfg.color}
              sx={{ height: 20, fontSize: '0.7rem', minWidth: 28 }} />
          </Tooltip>
        );
      },
    },
    {
      field: 'size', headerName: 'Size', width: 120,
      renderCell: (params) => {
        const gb = params.value / (1024 * 1024 * 1024);
        return gb >= 1 ? `${gb.toFixed(1)} GB` : `${(params.value / (1024 * 1024)).toFixed(0)} MB`;
      },
    },
    {
      field: 'modified', headerName: 'Modified', width: 160,
      renderCell: (params) => new Date(params.value * 1000).toLocaleString(),
    },
  ];

  const fileRows = (availableFiles || []).map((f, i) => ({ id: i, ...f }));

  return (
    <Box>
      <PipelineProgress />
      <PageHeader
        title="Create DCP Job"
        subtitle="Encode source to a Digital Cinema Package"
        actions={
          <Tooltip title="ISDCF naming convention — this string is what appears on the package the theater receives, and what every KDM and delivery receipt references. Updates live as you change form fields below.">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                ISDCF filename:
              </Typography>
              <Chip label={profileName} variant="outlined" size="small" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
            </Box>
          </Tooltip>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {probeData?.warnings?.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {probeData.warnings.map((w, i) => (
            <Alert key={i} severity="warning" icon={<WarningIcon />} sx={{ mb: 1 }}>{w}</Alert>
          ))}
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Source files + probe */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                1. Select Source
                {selectedFile && <Chip label={selectedFile} color="primary" size="small" sx={{ ml: 1 }} />}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <input id="dcp-source-upload" type="file" hidden onChange={handleSourceUpload} />
                <Button size="small" variant="outlined" component="label" htmlFor="dcp-source-upload">
                  Upload Source
                </Button>
                <Button size="small" onClick={() => dispatch(fetchDCPAvailableFiles())}>Refresh</Button>
              </Box>
            </Box>

            <DataGrid
              rows={fileRows}
              columns={fileColumns}
              autoHeight
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              {...(formData.reel_mode === 'by_source_file' ? {
                checkboxSelection: true,
                onRowSelectionModelChange: (ids) => {
                  const filenames = ids.map(id => fileRows.find(r => r.id === id)?.filename).filter(Boolean);
                  setSelectedFiles(filenames);
                  if (filenames.length > 0) handleFileSelect(filenames[0]);
                },
              } : {
                onRowClick: (params) => handleFileSelect(params.row.filename),
              })}
              sx={{
                '& .MuiDataGrid-row': { cursor: 'pointer' },
                '& .MuiDataGrid-row.Mui-selected': { bgcolor: `${theme.palette.primary.main}20 !important` },
              }}
            />
          </Paper>

          {/* Source analysis */}
          {(probing || probeData) && (
            <Paper sx={{ p: 3, mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Source Analysis</Typography>
              {probing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Analyzing source file...</Typography>
                </Box>
              ) : probeData && (
                <Grid container spacing={2}>
                  {probeData.video && (
                    <Grid size={6}>
                      <Typography variant="subtitle2" color="text.secondary">Video</Typography>
                      <Typography variant="body2">{probeData.video.codec} — {probeData.video.width}x{probeData.video.height}</Typography>
                      <Typography variant="body2">{probeData.video.frame_rate} fps</Typography>
                      <Typography variant="body2">Color: {probeData.video.color_primaries || 'unknown'} / {probeData.video.color_transfer || 'unknown'}</Typography>
                    </Grid>
                  )}
                  {probeData.audio && (
                    <Grid size={6}>
                      <Typography variant="subtitle2" color="text.secondary">Audio</Typography>
                      <Typography variant="body2">{probeData.audio.codec} — {probeData.audio.channels}ch</Typography>
                      <Typography variant="body2">Layout: {probeData.audio.channel_layout || 'unknown'}</Typography>
                      <Typography variant="body2">{probeData.audio.sample_rate} Hz / {probeData.audio.bit_depth}-bit</Typography>
                    </Grid>
                  )}
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Auto-derived DCP Settings</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={`Resolution: ${derivedRes}${sourceResLabel ? ` (from ${sourceResLabel})` : ''}`} size="small" variant="outlined" />
                      <Chip label={`Frame Rate: ${derivedFps} fps${sourceFpsLabel ? ` (from ${sourceFpsLabel})` : ''}`} size="small" variant="outlined" />
                      <Chip label={`Duration: ${Math.floor(probeData.duration_seconds / 60)}m ${Math.floor(probeData.duration_seconds % 60)}s`} size="small" variant="outlined" />
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Paper>
          )}
        </Grid>

        {/* DCP Settings */}
        <Grid size={{ xs: 12, md: 5 }}>
          {/* BASIC settings */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>2. DCP Settings</Typography>

            <TextField
              label="Project Name"
              fullWidth required
              value={formData.name}
              onChange={handleChange('name')}
              sx={{ mb: 1 }}
              inputProps={{ maxLength: 80 }}
              error={
                (!!formData.name && formData.name.trim().length < 2)
                || (!!formData.name && !isValidName(formData.name))
              }
              helperText={
                formData.name && !isValidName(formData.name)
                  ? `Contains invalid characters. Suggested: ${sanitizeName(formData.name)}`
                  : "Internal build label — used for filesystem paths, ISDCF DCP names, and operator filtering. Use a version string like 'CWM-200Mbps-v260503' to keep iterations distinguishable."
              }
            />

            {/* v10.18.6: Film title is what venues see on the delivery
                receipt. Distinct from Project Name (the internal build
                label). Blank ⇒ receipt falls back to Project Name. */}
            <TextField
              label="Film Title"
              fullWidth
              value={formData.film_title}
              onChange={(e) => {
                filmTitleTouched.current = true;
                handleChange('film_title')(e);
              }}
              sx={{ mb: 1 }}
              inputProps={{ maxLength: 255 }}
              placeholder="e.g. Come What May"
              helperText="Defaults to the active campaign name. The actual film title as it should appear on delivery receipts. Leave blank to use the Project Name above."
            />

            {/* Educational note when the title goes past 14 (only shown
                when strict mode is OFF). Plain-language explanation of
                the trade-off, not a stop sign. */}
            {!strictTitleLimit && formData.name.length > 14 && (
              <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                <Typography variant="caption">
                  <strong>Heads-up:</strong> your title is longer than the
                  cinema standard of 14 characters. Most modern theaters
                  and festivals handle this fine — but some older
                  projection systems (especially equipment from the early
                  2010s) only show the first 14 characters when they
                  display the film name on their internal screens. The
                  file itself still plays normally; only the on-screen
                  label gets shortened. If you're delivering to a known
                  modern venue or festival, you're good. If you want to
                  stay strictly safe across every projection chain, turn
                  on the toggle below.
                </Typography>
              </Alert>
            )}

            {/* Strict-mode toggle — opt-in cinema-standard 14-char cap.
                Default OFF (permissive with educational warning). Tooltip
                carries the deeper "why" without crowding the main UI. */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 0.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={strictTitleLimit}
                    onChange={(e) => setStrictTitleLimit(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    Stay strict at 14 characters (cinema standard)
                  </Typography>
                }
              />
              <Tooltip
                arrow
                placement="right"
                title={
                  <Box sx={{ p: 0.5, maxWidth: 360 }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                      What does this toggle do?
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                      The cinema industry agreed years ago that the short film name in a DCP filename should be 14 characters or less, so older projection equipment could display it without trimming. Modern projectors handle longer titles fine, so the default here lets you go up to 20 characters with a heads-up note when you go past 14.
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                      <strong>Toggle OFF (default):</strong> up to 20 characters, with a friendly note past 14. Works for almost every modern theater and festival.
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      <strong>Toggle ON:</strong> hard 14-character limit, the safest possible setting. Use this if you know you're shipping to older projection equipment or you want to be conservative.
                    </Typography>
                  </Box>
                }
              >
                <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>

            {/* Live preview. Two facts the user wants to see:
                  1. The "title slug" — what we'll use as the safe title
                     in the cinema-standard filename (no spaces, no
                     punctuation, dash-separated). Just a short string.
                  2. The full delivery filename — what the theater will
                     actually receive. Combines the slug with the rest
                     of the cinema metadata (audio config, resolution,
                     date, standard, type). */}
            {formData.name && (() => {
              const slug = deriveTitleSlug(formData.name, strictTitleLimit);
              const audio = formData.audio_layout === 'stereo' ? '20' : '51';
              const res = formData.resolution === '4K' ? '4K' : '2K';
              const std = (formData.standard || '').toUpperCase() === 'SMPTE' ? 'SMPTE' : 'IOP';
              const today = new Date();
              const date = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
              const fullFilename = `${slug || 'untitled'}_FTR-1_F-178_XX-XX_${audio}_${res}_${date}_${std}_OV.tar`;
              const slugChanged = slug !== formData.name.trim();
              return (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.default', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {slugChanged && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                        Theater-safe title (used in the filename)
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', display: 'block', mb: 1 }}>
                        {slug || '(empty)'}
                      </Typography>
                    </>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                    Delivery filename — what the theater receives
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', wordBreak: 'break-all' }}>
                    {fullFilename}
                  </Typography>
                </Box>
              );
            })()}

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Container</InputLabel>
                  <Select value={formData.container} label="Container" onChange={handleChange('container')}>
                    <MenuItem value="flat">Flat (1.85:1)</MenuItem>
                    <MenuItem value="scope">Scope (2.39:1)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Content Type</InputLabel>
                  <Select value={formData.content_type} label="Content Type" onChange={handleChange('content_type')}>
                    {CONTENT_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Derived settings summary */}
            <Box sx={{ p: 1.5, bgcolor: theme.palette.action.hover, borderRadius: 1, mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Auto-configured{probeData ? ' from source' : ' (select a file)'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2">Res: <strong>{formData.resolution}</strong></Typography>
                <Typography variant="body2">FPS: <strong>{formData.frame_rate}</strong></Typography>
                <Typography variant="body2">Standard: <strong>{formData.standard}</strong></Typography>
                <Typography variant="body2">Encrypted: <strong>{formData.encrypt ? 'Yes' : 'No'}</strong></Typography>
              </Box>
            </Box>


            {/* Audio layout — just the selector, not the full mapping table.
                renderValue keeps the closed-state label short so it doesn't
                truncate; full channel mapping shows in the dropdown options. */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Audio</InputLabel>
              <Tooltip title={formData.audio_layout === 'stereo'
                ? 'Stereo (20) — 8ch MXF: L R + silent C, LFE, Ls, Rs, HI, VI'
                : '5.1 (51) — 8ch MXF: L R C LFE Ls Rs HI VI'}>
                <Select
                  value={formData.audio_layout}
                  label="Audio"
                  onChange={handleOverride('audio_layout')}
                  renderValue={(v) => v === 'stereo' ? 'Stereo (20) — 8ch MXF' : '5.1 (51) — 8ch MXF'}
                >
                  <MenuItem value="5.1">5.1 (51) — 8ch MXF: L R C LFE Ls Rs HI VI</MenuItem>
                  <MenuItem value="stereo">Stereo (20) — 8ch MXF: L R + silent C LFE Ls Rs HI VI</MenuItem>
                </Select>
              </Tooltip>
            </FormControl>

            {/* Cinema-loudness advice banner — auto-populated when the
                source QC has loudness measurements. Tells the user what
                we measured, what the cinema target is, and why we picked
                the suggested gain. severity controls the visual weight:
                "high" (likely streaming master) is a real warning; lower
                severities are informational. */}
            {loudnessAdvice && loudnessAdvice.severity !== 'ok' && !stereoAdvice && (
              <Alert
                severity={loudnessAdvice.severity === 'high' ? 'warning' : 'info'}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Audio level recommendation: {loudnessAdvice.recommended_gain_db >= 0 ? '+' : ''}{loudnessAdvice.recommended_gain_db} dB
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {loudnessAdvice.reason}
                </Typography>
              </Alert>
            )}

            {/* Stereo-downmix advice — fires for stereo sources whose L/R
                Mid/Side ratio in the dialog band is high enough to suggest
                the stereo was made by downmixing a multichannel master with
                center summed into both speakers. We deliberately put the
                plain-English headline + summary + concrete recommendation
                FIRST, and tuck the cinema-engineering "why" behind a
                collapsible expander. Designed for non-industry creators
                (film school, indie) — they get a confident recommendation
                they can act on without needing to know what BS.1770 is,
                with the option to dive deeper if curious. */}
            {stereoAdvice && stereoAdvice.severity !== 'low' && (
              <Alert
                severity={
                  stereoAdvice.severity === 'critical' ? 'error'
                    : stereoAdvice.severity === 'high' ? 'warning'
                    : 'info'
                }
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  {stereoAdvice.headline}
                </Typography>
                <Typography variant="body2" sx={{ display: 'block', mb: 1 }}>
                  {stereoAdvice.summary}
                </Typography>
                {stereoAdvice.what_to_do && (
                  <Typography variant="body2" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                    {stereoAdvice.what_to_do}
                  </Typography>
                )}
                <Button
                  size="small"
                  onClick={() => setStereoDetailsOpen((v) => !v)}
                  sx={{ mt: 0.5, textTransform: 'none', p: 0, minWidth: 0 }}
                >
                  {stereoDetailsOpen ? 'Hide details' : 'Why does this matter?'}
                </Button>
                <Collapse in={stereoDetailsOpen}>
                  <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                      <strong>Why it matters:</strong> {stereoAdvice.details?.why_it_matters}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      <strong>How we measured it:</strong> {stereoAdvice.details?.technical}
                    </Typography>
                  </Box>
                </Collapse>
              </Alert>
            )}

            {/* Audio gain field — applied losslessly to every Content's
                <AudioGain> in the dcpomatic2 project XML. Auto-filled
                from the QC advice above. User can override; numeric
                input clamped client-side to [-12, +6] dB. */}
            <TextField
              fullWidth size="small" sx={{ mb: 2 }}
              type="number"
              label="Audio Gain (dB)"
              value={formData.audio_gain_db}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                const clamped = Number.isFinite(v) ? Math.max(-12, Math.min(6, v)) : 0;
                setFormData((prev) => ({ ...prev, audio_gain_db: clamped }));
                setOverrides((prev) => ({ ...prev, audio_gain_db: true }));
              }}
              inputProps={{ step: 0.5, min: -12, max: 6 }}
              helperText={
                stereoAdvice && stereoAdvice.severity !== 'low'
                  ? `Auto-suggested ${stereoAdvice.recommended_gain_db >= 0 ? '+' : ''}${stereoAdvice.recommended_gain_db} dB because your stereo source looks like a multichannel downmix (see the alert above). Override if your audio team has specific guidance.`
                  : loudnessAdvice
                    ? `Auto-suggested ${loudnessAdvice.recommended_gain_db >= 0 ? '+' : ''}${loudnessAdvice.recommended_gain_db} dB based on source measurement (${loudnessAdvice.measured_lufs} LUFS, cinema target ${loudnessAdvice.cinema_target_lufs} LUFS). Override if your audio team has specific guidance.`
                    : 'Theaters expect a specific audio level. 0 dB = no change. Lower it (negative numbers) if your stereo file was originally meant for streaming or YouTube.'
              }
            />


            {/* Reel Segmentation */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Reel Segmentation
              {formData.reel_mode !== 'single' && (
                <Chip label={formData.reel_mode.replace('by_', 'By ').replace('_', ' ')} size="small" sx={{ ml: 1 }} />
              )}
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Reel Mode</InputLabel>
              <Select value={formData.reel_mode} label="Reel Mode" onChange={handleOverride('reel_mode')}>
                <MenuItem value="single">Single Reel (recommended)</MenuItem>
                <MenuItem value="by_duration">By Duration (see warning below)</MenuItem>
                <MenuItem value="by_timecode">By Timecode</MenuItem>
                <MenuItem value="by_source_file">By Source File</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Most modern playback servers handle a single-reel DCP without issue. Multi-reel
              is only needed for older servers with storage constraints or when a venue specifically
              requests segmented reels.
            </Typography>
            {formData.reel_mode === 'by_duration' && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                <strong>By Duration</strong> requires a full pre-scan of the source file before
                encoding begins. For feature-length sources (100+ GB), this analysis phase can
                take hours during which the job will show 0% progress with no visible output.
                Unless the receiving venue has specifically requested segmented reels, use
                Single Reel for a much faster encode start.
              </Alert>
            )}

            {formData.reel_mode === 'by_duration' && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Reel Duration (minutes)" type="number" size="small" fullWidth
                  value={formData.reel_duration_minutes}
                  onChange={(e) => setFormData(f => ({ ...f, reel_duration_minutes: Math.max(5, Math.min(120, parseInt(e.target.value) || 20)) }))}
                  inputProps={{ min: 5, max: 120 }}
                  helperText={probeData?.duration_seconds
                    ? `~${Math.ceil(probeData.duration_seconds / 60 / formData.reel_duration_minutes)} reels (${formData.reel_duration_minutes * 60 * formData.frame_rate} frames each at ${formData.frame_rate}fps)`
                    : 'Select a source file to see reel count estimate'}
                />
              </Box>
            )}

            {formData.reel_mode === 'by_timecode' && (
              <Box sx={{ mb: 2 }}>
                {formData.reel_break_timecodes.map((tc, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TextField
                      size="small" placeholder="HH:MM:SS" value={tc}
                      onChange={(e) => {
                        const updated = [...formData.reel_break_timecodes];
                        updated[i] = e.target.value;
                        setFormData(f => ({ ...f, reel_break_timecodes: updated }));
                      }}
                      sx={{ width: 150 }}
                      inputProps={{ pattern: '\\d{2}:\\d{2}:\\d{2}' }}
                    />
                    <Typography variant="caption" color="text.secondary">Reel {i + 1} ends here</Typography>
                    <IconButton size="small" onClick={() => {
                      const updated = formData.reel_break_timecodes.filter((_, j) => j !== i);
                      setFormData(f => ({ ...f, reel_break_timecodes: updated }));
                    }}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                ))}
                <Button size="small" startIcon={<AddIcon />}
                  onClick={() => setFormData(f => ({ ...f, reel_break_timecodes: [...f.reel_break_timecodes, ''] }))}>
                  Add Break Point
                </Button>
                {formData.reel_break_timecodes.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {formData.reel_break_timecodes.length + 1} reels will be created
                  </Typography>
                )}
              </Box>
            )}

            {formData.reel_mode === 'by_source_file' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Select multiple source files below. Each file becomes one reel. Files must have matching resolution, frame rate, and color space.
                {selectedFiles.length > 0 && (
                  <Typography variant="body2" sx={{ mt: 1 }}><strong>{selectedFiles.length}</strong> files selected = {selectedFiles.length} reels</Typography>
                )}
              </Alert>
            )}

            {/* FFEC / FFMC markers — required for FTR */}
            {formData.content_type === 'FTR' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  SMPTE Markers
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>Required for Feature</Typography>
                </Typography>
                {/* v11.0.0 — plain-English explainer above the two
                    frame-number inputs. Audit: "users don't know what
                    FFEC or FFMC are, and the form provides no inline
                    help. A mistake here is not recoverable without
                    re-encoding." */}
                <Alert severity="info" icon={false} sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                  These frame numbers tell the projector where end credits start. A typo here means the
                  movie's "the end" cue plays in the wrong place — you'd need to re-encode to fix it. Look
                  up the frames in your edit, count from frame 0. (Compliance pre-check on the Sources page
                  usually auto-detects these; if the badge reads <strong>DCP ?</strong> you'll need to enter
                  them manually.)
                </Alert>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <TextField label="FFEC (frame)" type="number" fullWidth size="small"
                      value={formData.ffec_frame} onChange={handleChange('ffec_frame')}
                      helperText="First Frame of End Credits — e.g. 124400 for the first frame of the credit roll" />
                  </Grid>
                  <Grid size={6}>
                    <TextField label="FFMC (frame)" type="number" fullWidth size="small"
                      value={formData.ffmc_frame} onChange={handleChange('ffmc_frame')}
                      helperText="First Frame of Moving Credits — usually 5 seconds after FFEC" />
                  </Grid>
                </Grid>
              </>
            )}

            {/* ADVANCED */}
            <Accordion disableGutters elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, mt: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Advanced</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Resolution + Frame Rate overrides */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Override auto-detected values. Changes here affect the ISDCF package name.
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Resolution</InputLabel>
                      <Select value={formData.resolution} label="Resolution" onChange={handleOverride('resolution')}>
                        <MenuItem value="2K">2K</MenuItem>
                        <MenuItem value="4K">4K</MenuItem>
                      </Select>
                    </FormControl>
                    {derivedRes && formData.resolution !== derivedRes && (
                      <Typography variant="caption" color="warning.main">Source suggests {derivedRes}</Typography>
                    )}
                  </Grid>
                  <Grid size={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Frame Rate</InputLabel>
                      <Select value={formData.frame_rate} label="Frame Rate" onChange={handleOverride('frame_rate')}>
                        <MenuItem value={24}>24 fps</MenuItem>
                        <MenuItem value={25}>25 fps</MenuItem>
                        <MenuItem value={30}>30 fps</MenuItem>
                        <MenuItem value={48}>48 fps (HFR)</MenuItem>
                      </Select>
                    </FormControl>
                    {derivedFps && formData.frame_rate !== derivedFps && (
                      <Typography variant="caption" color="warning.main">Source suggests {derivedFps} fps</Typography>
                    )}
                  </Grid>
                  <Grid size={4}>
                    <TextField label="Bitrate (Mbit/s)" type="number" fullWidth size="small"
                      value={formData.video_bitrate} onChange={handleChange('video_bitrate')} />
                  </Grid>
                  <Grid size={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Standard</InputLabel>
                      <Select value={formData.standard} label="Standard" onChange={handleOverride('standard')}>
                        <MenuItem value="interop">Interop (most compatible)</MenuItem>
                        <MenuItem value="SMPTE">SMPTE (modern features)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Priority</InputLabel>
                      <Select value={formData.priority} label="Priority" onChange={handleChange('priority')}>
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="normal">Normal</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="urgent">Urgent</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Encryption granularity */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Encryption</Typography>
                <FormControlLabel
                  control={<Checkbox checked={formData.encrypt} onChange={handleChange('encrypt')} />}
                  label="Encrypt DCP (requires KDM per venue)"
                />
                {formData.encrypt && (
                  <Box sx={{ ml: 3 }}>
                    <FormControlLabel control={<Checkbox checked={formData.encrypt_picture} onChange={handleChange('encrypt_picture')} size="small" />} label={<Typography variant="body2">Picture</Typography>} />
                    <FormControlLabel control={<Checkbox checked={formData.encrypt_sound} onChange={handleChange('encrypt_sound')} size="small" />} label={<Typography variant="body2">Sound</Typography>} />
                    <FormControlLabel control={<Checkbox checked={formData.encrypt_text} onChange={handleChange('encrypt_text')} size="small" />} label={<Typography variant="body2">Subtitles/Text</Typography>} />
                  </Box>
                )}

                {/* Audio channel mapping */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Audio Channel Mapping</Typography>
                <TableContainer sx={{ maxHeight: 290 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: '0.75rem' }}>Ch</TableCell>
                        <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: '0.75rem' }}>Output</TableCell>
                        <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: '0.75rem' }}>Source</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentChannels.map((ch) => (
                        <TableRow key={ch.index}>
                          <TableCell sx={{ py: 0.25 }}>
                            <Chip label={ch.index} size="small"
                              sx={{ minWidth: 28, fontWeight: 600, fontSize: '0.7rem' }}
                              color={audioMapping[ch.index] === 'silent' ? 'default' : 'primary'}
                              variant={audioMapping[ch.index] === 'silent' ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.25 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              <strong>{ch.abbr}</strong> — {ch.label}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.25 }}>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={audioMapping[ch.index] ?? ch.sourceDefault}
                                onChange={(e) => setAudioMapping(prev => ({ ...prev, [ch.index]: e.target.value }))}
                                sx={{ fontSize: '0.75rem', height: 30 }}
                              >
                                {sourceOptions.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem' }}>{opt.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Language */}
                <Divider sx={{ my: 2 }} />
                <FormControl fullWidth size="small">
                  <InputLabel>Audio Language</InputLabel>
                  <Select value={formData.audio_language} label="Audio Language" onChange={handleChange('audio_language')}>
                    {['English','Spanish','French','German','Italian','Japanese','Korean','Mandarin','Portuguese'].map(l => (
                      <MenuItem key={l} value={l}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </AccordionDetails>
            </Accordion>

            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
              {(() => {
                const missing = [];
                if (!formData.name.trim()) missing.push('project name');
                if (formData.reel_mode === 'by_source_file') {
                  if (selectedFiles.length < 2) missing.push('at least 2 source files');
                } else if (!selectedFile) {
                  missing.push('source file');
                }
                const disabled = submitting || missing.length > 0;
                const tip = disabled && !submitting ? `Missing: ${missing.join(', ')}` : 'Create DCP Job';
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                    <Tooltip title={tip} arrow>
                      <span>
                        <Button variant="contained" onClick={handleSubmit} disabled={disabled} size="large">
                          {submitting ? <CircularProgress size={24} /> : 'Create DCP Job'}
                        </Button>
                      </span>
                    </Tooltip>
                    {/* v11.0.0 — operator audit: "a user who filled out
                        all the settings and forgot to pick a source
                        won't know what's blocking them." The Tooltip
                        only fires on hover; surface the same reason
                        as visible caption text below the button. */}
                    {disabled && !submitting && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        data-testid="create-dcp-disabled-reason"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        Missing: {missing.join(', ')}.
                      </Typography>
                    )}
                  </Box>
                );
              })()}
              <Button variant="outlined" onClick={() => navigate('/dcp')}>Cancel</Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CreateDCPJob;
